import { Typography } from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import type {
    GridCallbackDetails,
    GridCellParams,
    GridColDef,
    GridColumnVisibilityModel,
    GridPaginationModel,
    GridRowSelectionModel
} from '@mui/x-data-grid-premium';
import type { Dispatch, SetStateAction } from 'react';
import React, { useCallback, useMemo, useState } from 'react';
import { KEY_ROLES_COLUMN, MATERIAL_COLUMN } from '../../../production/helpers/productionImportUtils';
import { DATA_SOURCE_LABELS } from '../../../shortlists/ShortlistConstants';
import { acceptedFilesMock } from '../../attachments/attachmentsApi';
import Confirm from '../../components/Confirm';
import UploadFile from '../../components/upload/UploadFile';
import UploadIconButton from '../../components/upload/UploadIconButton';
import { FormContainer } from '../../form/FormContainer';
import FormItem from '../../form/FormItem';
import FormWidget from '../../form/FormWidget';
import { useI18n } from '../../i18n/I18n';
import type { KeyCreditsWrapLimit } from '../../keyCredits';
import { KEY_CREDITS_GRID_FIELD } from '../../keyCredits/constants';
import { C, H, V } from '../../Layout';
import DataGrid from '../../list/DataGrid';
import type { Reference } from '../../reference/reference';
import { successToastMessage } from '../../utils/commonUtils';
import { RawLineItem } from '../../utils/resourceShortlistImport';
import type { ImportRow } from '../types';
import WrapLimitColumnMenu from './WrapLimitColumnMenu';

const DEFAULT_AS_IS_COLUMNS: string[] = [KEY_CREDITS_GRID_FIELD, MATERIAL_COLUMN, KEY_ROLES_COLUMN];
const DATA_GRID_CONTAINER_SX: SxProps<Theme> = { borderRadius: '8px' };
const FILL_DATA_GRID_CONTAINER_SX: SxProps<Theme> = { borderRadius: '8px', height: '100%' };
const FILL_GRID_SECTION_SX: SxProps<Theme> = { flex: 1, minHeight: 0 };
const REVIEW_GRID_SECTION_SX: SxProps<Theme> = { height: '100%' };
const SELECT_GRID_SECTION_SX: SxProps<Theme> = { height: '80%' };
const UPLOAD_FILE_TITLE_SX: SxProps<Theme> = { textTransform: 'uppercase' };
const WRAP_LIMIT_COLUMN_NAMES = ['about'];

function getGridSectionSx(isReviewAndEdit: boolean, isTitleAndDetailsVisible: boolean): SxProps<Theme> {
    if (isReviewAndEdit && !isTitleAndDetailsVisible) {
        return FILL_GRID_SECTION_SX;
    }
    return isReviewAndEdit ? REVIEW_GRID_SECTION_SX : SELECT_GRID_SECTION_SX;
}

export type SelectionAndReviewSectionProps = {
    feature: string;
    isReviewAndEdit: boolean;
    category?: string;
    dataSource?: string;
    selectedRows: ImportRow[];
    rows: RawLineItem[];
    defaultColumns: GridColDef[];
    columnPreferences: Record<string, boolean>;
    showColumnsBasedOnPreference: (defaultColumns: GridColDef[]) => GridColDef[];
    selectionModel: GridRowSelectionModel;
    handleSelectionChange: (rowSelectionModel: GridRowSelectionModel, details?: GridCallbackDetails) => void;
    handleDelete: (id: string) => void;
    page: number;
    paginationModel: GridPaginationModel;
    setPaginationModel: Dispatch<SetStateAction<GridPaginationModel>>;
    setPage: Dispatch<SetStateAction<number>>;
    pageSize: number;
    setPageSize: Dispatch<SetStateAction<number>>;
    setSelectedRows: Dispatch<SetStateAction<ImportRow[]>>;
    handleFileUploadChange?: (fileArray: File[], references: Reference[]) => void;
    hasDataToImport: boolean;
    isTitleAndDetailsVisible?: boolean;
    externalColumnVisibilityModel?: GridColumnVisibilityModel;
    externalOnColumnVisibilityModelChange?: Dispatch<SetStateAction<GridColumnVisibilityModel>>;
    asIsColumns?: string[];
    isCellEditable?: (params: GridCellParams) => boolean;
    aboutWrapLimit?: KeyCreditsWrapLimit;
    setAboutWrapLimit?: (value: KeyCreditsWrapLimit) => void;
    showUploadButton?: boolean;
    shouldScrollRight?: boolean;
    saveConfirmationOpen?: boolean;
    setSaveConfirmationOpen?: Dispatch<SetStateAction<boolean>>;
    handleDoSave?: () => void;
};

export default function SelectionAndReviewSection({
    feature,
    isReviewAndEdit,
    category,
    dataSource,
    selectedRows,
    rows,
    defaultColumns,
    columnPreferences,
    showColumnsBasedOnPreference,
    selectionModel,
    handleSelectionChange,
    handleDelete,
    page,
    paginationModel,
    setPaginationModel,
    setPage,
    pageSize,
    setPageSize,
    setSelectedRows,
    handleFileUploadChange = undefined,
    hasDataToImport,
    isTitleAndDetailsVisible = true,
    externalColumnVisibilityModel = undefined,
    externalOnColumnVisibilityModelChange = undefined,
    asIsColumns = DEFAULT_AS_IS_COLUMNS,
    isCellEditable = undefined,
    aboutWrapLimit = undefined,
    setAboutWrapLimit = undefined,
    showUploadButton = false,
    shouldScrollRight = false,
    saveConfirmationOpen = false,
    setSaveConfirmationOpen = undefined,
    handleDoSave = undefined
}: SelectionAndReviewSectionProps) {
    const showAboutColumnMenu = aboutWrapLimit !== undefined && setAboutWrapLimit !== undefined;
    const [, setIsUploading] = useState(false);
    const t = useI18n();
    const importSelectionHeading = t('import.contacts.select.heading', { category });
    const importReviewAndEditHeading = t('import.contacts.preview.heading', { category });
    const importSelectionSubheading = t('import.contacts.select.subheading', {
        source: dataSource || DATA_SOURCE_LABELS.UPLOAD,
        category: category?.toLowerCase() ?? ''
    });
    const importSelectionSubheading1 = t('import.contacts.select.subheading1', {
        category: category?.toLowerCase() ?? ''
    });
    const importReviewAndEditSubheading = t('import.contacts.preview.subheading', {
        source: dataSource || DATA_SOURCE_LABELS.UPLOAD,
        category: category?.toLowerCase() ?? ''
    });
    const importUploadFileHeading = t('import.upload.file.heading');
    const saveConfirmationTitle = t('import.material.save.confirm.title');
    const saveConfirmationMessage = t('import.material.save.confirm.message');

    const fillGridSection = isReviewAndEdit && !isTitleAndDetailsVisible;
    const gridSectionSx = getGridSectionSx(isReviewAndEdit, isTitleAndDetailsVisible);

    const handleConfirmSave = useCallback(() => {
        setSaveConfirmationOpen?.(false);
        handleDoSave?.();
    }, [setSaveConfirmationOpen, handleDoSave]);

    const handleCancelSave = useCallback(() => {
        setSaveConfirmationOpen?.(false);
    }, [setSaveConfirmationOpen]);

    const handlePaginationModelChange = useCallback(
        (nextPage: number, nextPageSize: number) => {
            setPaginationModel({ page: nextPage, pageSize: nextPageSize });
            setPage(nextPage);
            setPageSize(nextPageSize);
        },
        [setPaginationModel, setPage, setPageSize]
    );

    const handlePageChange = useCallback(
        (nextPage: number) => handlePaginationModelChange(nextPage, pageSize),
        [handlePaginationModelChange, pageSize]
    );

    const handlePageSizeChange = useCallback(
        (nextPageSize: number) => handlePaginationModelChange(page, nextPageSize),
        [handlePaginationModelChange, page]
    );

    const handleUploadComplete = useCallback(
        (files: File[], references) => {
            successToastMessage('attachment.files.upload.success');
            handleFileUploadChange?.(files, references);
        },
        [handleFileUploadChange]
    );

    const dataGridSlots = useMemo(
        () => (showAboutColumnMenu ? { columnMenu: WrapLimitColumnMenu } : undefined),
        [showAboutColumnMenu]
    );

    const dataGridSlotProps = useMemo(
        () =>
            showAboutColumnMenu
                ? {
                      columnMenu: {
                          wrapLimitColumnNames: WRAP_LIMIT_COLUMN_NAMES,
                          wrapLimit: { about: aboutWrapLimit },
                          onWrapLimitChange: (_field: string, value: KeyCreditsWrapLimit) => setAboutWrapLimit?.(value)
                      }
                  }
                : undefined,
        [showAboutColumnMenu, aboutWrapLimit, setAboutWrapLimit]
    );

    return (
        <>
            {isTitleAndDetailsVisible && (
                <V>
                    <FormContainer>
                        <FormItem>
                            <Typography fontSize={24} fontWeight={600}>
                                {isReviewAndEdit ? importReviewAndEditHeading : importSelectionHeading}
                            </Typography>
                        </FormItem>
                        <FormItem>
                            <C>
                                {!isReviewAndEdit && (
                                    <>
                                        <Typography fontSize={14} fontWeight={400}>
                                            {importSelectionSubheading}
                                        </Typography>
                                        <Typography fontSize={14} fontWeight={400}>
                                            {importSelectionSubheading1}
                                        </Typography>
                                    </>
                                )}
                                {isReviewAndEdit && (
                                    <Typography dangerouslySetInnerHTML={{ __html: importReviewAndEditSubheading }} />
                                )}
                            </C>
                        </FormItem>
                    </FormContainer>
                </V>
            )}
            <V gap={2} sx={gridSectionSx}>
                {showUploadButton && isReviewAndEdit && (
                    <V>
                        <Typography dangerouslySetInnerHTML={{ __html: importUploadFileHeading }} />
                        <H gap={1}>
                            <FormItem>
                                <FormWidget
                                    component="SectionTitle"
                                    i18nToken="upload.missing.files"
                                    containerSx={UPLOAD_FILE_TITLE_SX}
                                />
                            </FormItem>
                            <UploadFile
                                UploadIconComponent={UploadIconButton}
                                setIsUploading={setIsUploading}
                                acceptedFiles={acceptedFilesMock?.join(',')}
                                onUploadComplete={handleUploadComplete}
                                entityId={''}
                                multiple
                            />
                        </H>
                    </V>
                )}
                <DataGrid
                    id="data-import-grid"
                    containerSx={fillGridSection ? FILL_DATA_GRID_CONTAINER_SX : DATA_GRID_CONTAINER_SX}
                    feature={feature}
                    rowCount={isReviewAndEdit ? selectedRows.length : rows.length}
                    rows={isReviewAndEdit ? selectedRows : rows}
                    columns={
                        hasDataToImport
                            ? defaultColumns
                            : columnPreferences
                              ? showColumnsBasedOnPreference(defaultColumns)
                              : defaultColumns
                    }
                    checkboxSelection={!isReviewAndEdit}
                    includeActions={isReviewAndEdit}
                    enableRichTextEditing
                    enableInlineEditing={isReviewAndEdit}
                    onDelete={isReviewAndEdit ? handleDelete : undefined}
                    rowSelectionModel={selectionModel}
                    onRowSelectionModelChange={!isReviewAndEdit ? handleSelectionChange : undefined}
                    serverPagination={false}
                    page={page}
                    paginationModel={paginationModel}
                    onPageChange={handlePageChange}
                    pageSize={pageSize}
                    onPageSizeChange={handlePageSizeChange}
                    setRows={
                        isReviewAndEdit
                            ? (newRows) => {
                                  const cleanedRows = newRows.map((row) => {
                                      Object.keys(row).forEach((key) => {
                                          if (row[key] === undefined) {
                                              row[key] = '';
                                          }
                                      });
                                      return row;
                                  });
                                  setSelectedRows(cleanedRows);
                              }
                            : undefined
                    }
                    noWrapColValue={false}
                    asIsColumns={asIsColumns}
                    externalColumnVisibilityModel={externalColumnVisibilityModel}
                    externalOnColumnVisibilityModelChange={externalOnColumnVisibilityModelChange}
                    isCellEditable={isCellEditable}
                    shouldScrollRight={shouldScrollRight}
                    slots={dataGridSlots}
                    slotProps={dataGridSlotProps}
                />
                <Confirm
                    open={saveConfirmationOpen}
                    title={saveConfirmationTitle}
                    okLabelI18n="import.material.confirm.anyway"
                    body={<Typography>{saveConfirmationMessage}</Typography>}
                    onConfirm={handleConfirmSave}
                    onCancel={handleCancelSave}
                />
            </V>
        </>
    );
}
